import { ethers } from "ethers";
import MetaMaskSDK from '@metamask/sdk';
import DepositRewardSystemABI from '../abis/DepositRewardSystemABI.json' with { type: 'json' };
import NdeipiCoinABI from '../abis/NdeipiTokenABI.json' with { type: 'json' };

const chainType = process.env.REACT_APP_BLOCKCHAIN_NETWORK || 'evm'; 

// Initialize MetaMask SDK
const MMSDK = new MetaMaskSDK({
  injectProvider: true,
  dappMetadata: {
    name: "Ndeipi Wallet",
    url:chainType === 'evm' ? "https://wallet.ndeipi.com" : "https://believe-in-unity.netlify.app/",
  }
});

class SmartContractService {
  private static instance: SmartContractService;
  private depositsContract: ethers.Contract | null = null;
  private ndeipiTokenContract: ethers.Contract | null = null;
  private provider: ethers.BrowserProvider | ethers.JsonRpcProvider | null = null;
  private signer: ethers.Signer | null = null;
  private isMobile: boolean = false;
  private isConnected: boolean = false;

  private constructor() {}

  public static getInstance(): SmartContractService {
    if (!SmartContractService.instance) {
      SmartContractService.instance = new SmartContractService();
    }
    return SmartContractService.instance;
  }

  // Get the Ethereum provider (MetaMask)
  private getEthereum() {
    if (typeof window !== 'undefined') {
      return window.ethereum || MMSDK.getProvider();
    }
    return null;
  }

  // Initialize connection to MetaMask
  public async connectWallet(): Promise<void> {
    const ethereum = this.getEthereum();
    if (!ethereum) {
      throw new Error('MetaMask not detected');
    }

    this.isMobile = /Mobi|Android/i.test(navigator.userAgent);
    
    try {
      const accounts = await ethereum.request({ method: 'eth_requestAccounts' }) as string[];
      if (accounts.length > 0) {
        this.provider = new ethers.BrowserProvider(ethereum);
        console.log('Network is %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%% ', await this.provider.getNetwork());
        this.signer = await this.provider.getSigner();
        this.isConnected = true;
      }
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      throw error;
    }
  }

  // Check current connection status
  public async checkConnection(): Promise<{ isConnected: boolean; isMobile: boolean }> {
    const ethereum = this.getEthereum();
    if (!ethereum) {
      return { isConnected: false, isMobile: false };
    }

    this.isMobile = /Mobi|Android/i.test(navigator.userAgent);
    
    try {
      const accounts = await ethereum.request({ method: 'eth_accounts' }) as string[];
      this.isConnected = accounts.length > 0;
      console.log('Current accounts:', accounts);
      if (this.isConnected && !this.provider) {
        this.provider = new ethers.BrowserProvider(ethereum);
        this.signer = await this.provider.getSigner();
      }
    } catch (error) {
      console.error('Failed to check connection:', error);
      this.isConnected = false;
    }

    return { isConnected: this.isConnected, isMobile: this.isMobile };
  }

  // Initialize contracts
  public async initializeContracts(): Promise<void> {
    if (!this.signer) {
      throw new Error('Wallet not connected');
    }

    try {
        console.log('Initializing contracts with signer:', this.signer);
      this.depositsContract = new ethers.Contract(
        process.env.REACT_APP_CONTRACT_ADDRESS!,
        DepositRewardSystemABI,
        this.signer
      );

      this.ndeipiTokenContract = new ethers.Contract(
        process.env.REACT_APP_NDEIPICOIN_CONTRACT_ADDRESS!,
        NdeipiCoinABI,
        this.signer
      );
        console.log('Contracts initialized successfully');
    } catch (error) {
      console.error('Failed to initialize contracts:', error);
      throw error;
    }
  }

  // Add this method to your SmartContractService class
public async initializeContractsInternal(
  privateKey: string,
  walletAddress: string 
): Promise<void> {
  try {

    const  rpcUrl: string = process.env.REACT_APP_POLYGON_RPC_URL || 'https://polygon-rpc.com/';

    if(this.depositsContract &&this.ndeipiTokenContract) {
    // console.log('Contracts already initialized, skipping initialization');
        return; // Contracts already initialized
    }

    // Create a provider using the RPC URL
    this.provider = new ethers.JsonRpcProvider(rpcUrl);
    
    // Create a wallet from the private key and connect it to the provider
    this.signer = new ethers.Wallet(privateKey, this.provider);
    
    // Verify the wallet address matches
    const derivedAddress = await this.signer.getAddress();
    if (derivedAddress.toLowerCase() !== walletAddress.toLowerCase()) {
      throw new Error('Private key does not match the provided wallet address');
    }

    // console.log('Initializing contracts with private key for address:', walletAddress);
    
    // Initialize contracts
    this.depositsContract = new ethers.Contract(
      process.env.REACT_APP_CONTRACT_ADDRESS!,
      DepositRewardSystemABI,
      this.signer
    );

    this.ndeipiTokenContract = new ethers.Contract(
      process.env.REACT_APP_NDEIPICOIN_CONTRACT_ADDRESS!,
      NdeipiCoinABI,
      this.signer
    );

    this.isConnected = true;
    // console.log('Contracts initialized successfully with private key');
  } catch (error) {
    console.error('Failed to initialize contracts with private key:', error);
    throw error;
  }
}

  // Get initialized contracts
  public getDepositsContract(): ethers.Contract {
    if (!this.depositsContract) {
      throw new Error('Deposits contract not initialized');
    }
    return this.depositsContract;
  }

  public getNdeipiTokenContract(): ethers.Contract {
    if (!this.ndeipiTokenContract) {
      throw new Error('NdeipiToken contract not initialized');
    }
    return this.ndeipiTokenContract;
  }

  // Get current signer address
  public async getSignerAddress(): Promise<string> {
    if (!this.signer) {
      throw new Error('Wallet not connected');
    }
    return await this.signer.getAddress();
  }

  // Get connection status
  public getConnectionStatus() {
    return {
      isMobile: this.isMobile,
      isConnected: this.isConnected
    };
  }
}

export default SmartContractService.getInstance();